// Layer-by-layer pull progress: parses `docker pull` output and renders
// each layer as its own progress row. Speaks DevOps-native UI.

import { Box, Group, Progress, Text } from '@mantine/core'
import { IconCloudDownload } from '@tabler/icons-react'
import { motion } from 'framer-motion'
import { useMemo } from 'react'
import {
  LAYER_STATUS_LABEL,
  formatBytes,
  parseDockerPullOutput,
} from './parseDockerPullOutput'
import type { LayerStatus } from './parseDockerPullOutput'
import { COLOR } from './tokens'
import { formatElapsed, useTicker } from './useTicker'

interface Props {
  output: string
  /** ISO timestamp when the pulling phase started — drives elapsed counter
   *  used in the empty-output placeholder (SSM doesn't stream partial). */
  startedAt?: string
}

const STATUS_RANK: Record<LayerStatus, number> = {
  done: 4,
  extracting: 3,
  download_done: 2,
  verifying: 2,
  downloading: 1,
  pulling: 0,
}

const STATUS_COLOR: Record<LayerStatus, string> = {
  pulling: COLOR.muted,
  downloading: COLOR.brandAzul,
  verifying: COLOR.brandVerde,
  download_done: COLOR.brandVerde,
  extracting: COLOR.brandAzul,
  done: COLOR.brandVerde,
}

export function PullProgress({ output, startedAt }: Props) {
  const parsed = useMemo(() => parseDockerPullOutput(output), [output])

  if (parsed.layers.length === 0) {
    return <PullPlaceholder startedAt={startedAt} />
  }

  // Sort: in-progress first (downloading/extracting), then verifying/dl_done,
  // then done. Within bucket: keep input order.
  const sorted = [...parsed.layers].sort(
    (a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status],
  )

  const total = parsed.layers.length
  const done = parsed.layers.filter(l => l.status === 'done').length

  return (
    <Box>
      <Group justify="space-between" mb={6}>
        <Text size="xs" fw={600} c="dimmed">
          Layers ({done} / {total})
        </Text>
        {parsed.complete && (
          <Text
            size="xs"
            fw={700}
            c="inovareTeal.7"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            ✓ pull complete
          </Text>
        )}
      </Group>
      <Box
        component="ul"
        m={0}
        p={0}
        style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}
      >
        {sorted.map(layer => {
          const pct = layer.progress
            ? Math.min(100, (layer.progress.current / layer.progress.total) * 100)
            : layer.status === 'done' || layer.status === 'download_done'
            ? 100
            : 0
          return (
            <motion.li
              key={layer.id}
              layout
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Group gap="xs" wrap="nowrap" align="center">
                <Text
                  size="xs"
                  style={{
                    fontFamily: '"JetBrains Mono", monospace',
                    width: 92,
                    flexShrink: 0,
                    color: 'var(--ai-text-secondary, #5f7882)',
                  }}
                >
                  {layer.id.slice(0, 12)}
                </Text>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Progress
                    value={pct}
                    color={STATUS_COLOR[layer.status]}
                    size="sm"
                    radius="xl"
                    striped={layer.status === 'downloading' || layer.status === 'extracting'}
                    animated={layer.status === 'downloading' || layer.status === 'extracting'}
                  />
                </Box>
                <Text
                  size="xs"
                  style={{
                    width: 110,
                    flexShrink: 0,
                    fontFamily: '"JetBrains Mono", monospace',
                    color: 'var(--ai-text-secondary, #5f7882)',
                    textAlign: 'right',
                  }}
                >
                  {layer.progress
                    ? `${formatBytes(layer.progress.current)} / ${formatBytes(layer.progress.total)}`
                    : LAYER_STATUS_LABEL[layer.status]}
                </Text>
              </Group>
            </motion.li>
          )
        })}
      </Box>
    </Box>
  )
}

/**
 * Placeholder shown while `docker pull` hasn't emitted anything yet.
 *
 * Honest about the SSM limitation: `get_command_invocation` returns an
 * empty `StandardOutputContent` until the command finishes. So there's
 * no "live tail" possible with the current backend — the layer view
 * appears all at once when pulling completes. We tell the operator
 * exactly that, and show an elapsed-time counter so they can tell the
 * process is alive.
 */
function PullPlaceholder({ startedAt }: { startedAt?: string }) {
  useTicker(1000)
  const elapsedMs = startedAt ? Date.now() - new Date(startedAt).getTime() : 0
  return (
    <Box
      p={14}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        textAlign: 'center',
      }}
    >
      <motion.div
        animate={{ y: [0, -2, 0], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        style={{ color: COLOR.brandAzul }}
      >
        <IconCloudDownload size={20} />
      </motion.div>
      <Text size="xs" fw={600} c="inovareTeal.7">
        Pull em andamento{startedAt ? ` • ${formatElapsed(elapsedMs)}` : ''}
      </Text>
      <Text
        size="xs"
        c="dimmed"
        style={{ fontStyle: 'italic', maxWidth: 360, lineHeight: 1.4 }}
      >
        SSM não transmite output durante a execução — o detalhamento por layer
        aparece de uma vez quando o pull termina.
      </Text>
    </Box>
  )
}
